import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QueryParams } from '@/lib/api';

/**
 * Kernel-Stand aus Zabbix (GET /integrations/zabbix/kernel-status).
 *
 * Types lokal, weil der Endpunkt parallel entsteht und api.gen.ts
 * nicht angefasst werden darf.
 */

export interface KernelStatusRow {
  system_id: string;
  customer_slug: string;
  customer_name: string;
  hostname: string;
  service_type: string | null;
  os_name: string | null;
  zabbix_host: string | null;
  matched_by: string | null;
  kernel_running: string | null;
  kernel_version: string | null;
  kernel_ok: boolean | null;
  reboot_required: boolean | null;
  pending_updates: number | null;
  security_updates: number | null;
  last_seen: string | null;
}

export interface KernelStatusSummary {
  hosts_total: number;
  matched: number;
  unmatched: number;
  ok: number;
  not_ok: number;
  reboot_required: number;
  unknown: number;
}

export interface KernelStatusResponse {
  rows: KernelStatusRow[];
  fetched_at: string;
  summary: KernelStatusSummary;
}

export interface KernelStatusFilter {
  customerSlug?: string;
  onlyPending?: boolean;
  kernelTarget?: string;
}

export const kernelStatusKeys = {
  all: ['kernel-status'] as const,
  list: (filter: KernelStatusFilter) => ['kernel-status', 'list', filter] as const,
};

function filterToParams(filter: KernelStatusFilter): QueryParams {
  const params: QueryParams = {};
  if (filter.customerSlug) params.customer_slug = filter.customerSlug;
  // false nicht mitsenden: der Schalter heißt "nur ausstehend", Default ist aus.
  if (filter.onlyPending) params.only_pending = true;
  if (filter.kernelTarget) params.kernel_target = filter.kernelTarget;
  return params;
}

export function useKernelStatus(
  filter: KernelStatusFilter = {}
): ReturnType<typeof useQuery<KernelStatusResponse>> {
  return useQuery<KernelStatusResponse>({
    queryKey: kernelStatusKeys.list(filter),
    queryFn: () =>
      api.get<KernelStatusResponse>('/integrations/zabbix/kernel-status', filterToParams(filter)),
    staleTime: 30_000,
  });
}
